import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/use-api";
import {
  AssessmentType,
  CreateAssessmentInput,
  CriteriaTarget,
} from "@/types/assessment";
import { Rubric } from "@/types/rubric";
import * as Sentry from "@sentry/react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Step1Details } from "./Step1Details";
import { Step2Content } from "./Step2Content";
import { Step3Settings } from "./Step3Settings";

interface AssessmentWizardProps {
  courseId: string;
  onCancel: () => void;
  onComplete: () => void;
}

export type WizardData = {
  title: string;
  type: AssessmentType;
  agentId: string;
  instructions: { markdown: string; fileType: string };
  instructionsFileName: string | null;
  rubric: Rubric | null;
  rubricFileName: string | null;
  groupBySection: boolean;
  shareAnnotations: boolean;
  showSuggestions: boolean;
  shareFeedback: boolean;
  shareCriteria: boolean;
};

export const AssessmentWizard = ({
  courseId,
  onCancel,
  onComplete,
}: AssessmentWizardProps) => {
  const { createAssessment } = useApi();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [data, setData] = useState<WizardData>({
    title: "",
    type: "written",
    agentId: "",
    instructions: { markdown: "", fileType: "" },
    instructionsFileName: null,
    rubric: null,
    rubricFileName: null,
    groupBySection: false,
    shareAnnotations: false,
    showSuggestions: false,
    shareFeedback: true,
    shareCriteria: true
  });

  const totalSteps = data.type === "oral" ? 2 : 3;

  const updateData = (updates: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!data.rubric) return;

    setIsSubmitting(true);
    try {
      const payload: CreateAssessmentInput = {
        title: data.title,
        type: data.type,
        agentId: data.agentId || undefined,
        instructions: data.instructions,
        feedbackLive: false,
        rubric: {
          title: data.rubric.title,
          markdown: data.rubric.markdown,
          criteria: data.rubric.criteria.map((c) => {
            let target = c.target as CriteriaTarget | undefined;
            if (data.type === "written") target = "document";
            if (data.type === "oral") target = "transcript";
            // For hybrid, use the user selected target (defaulted to document in Step 2)
            return { ...c, target: target || "document" };
          }),
        },
        groupBySection: data.groupBySection,
        shareAnnotations: data.shareAnnotations,
        showSuggestions: data.showSuggestions,
        shareCriteria: data.shareCriteria,
        shareFeedback: data.shareFeedback,
        shareTranscript:
          data.type === "oral" || data.type === "hybrid" ? true : false
      };

      await createAssessment(courseId, payload);
      onComplete();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toast.error("Failed to create assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step validation
  const canContinue = () => {
    console.log("canContinue", step, data);
    if (step === 1) {
      if (!data.title.trim()) return false;
      if ((data.type === "hybrid" || data.type === "oral") && !data.agentId)
        return false;
      return true;
    }
    if (step === 2) {
      if (!data.instructions.markdown || !data.rubric) return false;
      if (data.type === "hybrid") {
        // Validation for criteria mapping
        // At least one document and one transcript
        const criteria = data.rubric.criteria;
        const hasDocument = criteria.some((c) => c.target === "document");
        const hasTranscript = criteria.some((c) => c.target === "transcript");
        return hasDocument && hasTranscript;
      }
      return true;
    }
    return true;
  };

  return (
    <div className="max-w-3xl mx-auto py-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold tracking-tight">
          Create an assessment
        </h2>
        <span className="text-muted-foreground">Step {step} / {totalSteps}</span>
      </div>

      <div className="py-4 min-h-[400px]">
        {step === 1 && <Step1Details data={data} updateData={updateData} />}
        {step === 2 && <Step2Content data={data} updateData={updateData} />}
        {step === 3 && <Step3Settings data={data} updateData={updateData} />}
        {isSubmitting && data.type === "hybrid" && (
          <div className="border rounded-md p-6 space-y-4 mt-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="font-medium">
                Creating sample submission for agent preview
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
              We’re preparing a sample response that can be used to preview the
              agent playbook.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 mt-8">
        <Button
          variant={step > 1 ? "ghost" : "outline"}
          className={step > 1 ? "text-destructive hover:text-destructive" : undefined}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        {step > 1 && (
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        {step < totalSteps ? (
          <Button onClick={handleNext} disabled={!canContinue()}>
            Continue
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canContinue()}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create assessment
          </Button>
        )}
      </div>
    </div>
  );
};
